import { createSchema } from "../../components/srf/editSrf/submitHandlers";
import { BASE_URL } from "../../global";
import { createSRFAnalytics } from "../../utils/analytics/srfAnalytics";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { getDataFromSqlJs } from "../offline/fetchHandlers";
import { executeSqlInSqlJs } from "../offline/submitHandlers";
import { getSrfsWithFullData } from "./fetchHandlers";

export const submitOfflineToServer = async (setLoader) => {
  setLoader(true);

  let syncSuccess = false; // ✅ flag to track success

  try {
    // ✅ Step 1: Collect datasheetIds and related rows first
    const datasheetIdsData = await getDataFromSqlJs(
      "SELECT * FROM datasheetIds",
    );
    const datasheetIds = datasheetIdsData.map((d) => d.id);

    let datasheetsRows = [];
    let certificatesRows = [];
    let readingsRows = [];

    if (datasheetIds.length) {
      datasheetsRows = await getDataFromSqlJs(
        `SELECT * FROM datasheets WHERE id IN (${datasheetIds.join(",")})`,
      );

      certificatesRows = await getDataFromSqlJs(
        `SELECT * FROM certificates WHERE id IN (${datasheetIds.join(",")})`,
      );

      readingsRows = await getDataFromSqlJs(
        `SELECT * FROM datasheetStaticReadings WHERE datasheetId IN (${datasheetIds.join(
          ",",
        )})`,
      );
    }

    // ✅ Step 2: SRF sync loop
    const srfWithAllData = await getSrfsWithFullData();

    if (!Array.isArray(srfWithAllData) || srfWithAllData.length === 0) {
      console.warn("No offline data found to submit.");
    } else {
      for (const {
        srfsData,
        srfInstrumentsData,
        datasheetsData,
        certificatesData,
        datasheetStaticReadingsData,
      } of srfWithAllData) {
        const { id, ...srfsPayload } = srfsData;

        // 1️⃣ Insert SRF
        const { data: srfsInsertRes } = await axiosWithToken.post(
          `${BASE_URL}srfs`,
          srfsPayload,
        );

        const srfId = srfsInsertRes?.insertId;

        // 2️⃣ Get serviceReqNumber
        const { data: serviceReqData } = await axiosWithToken.post(
          `${BASE_URL}dynamic`,
          { query: `SELECT serviceReqNumber FROM srfs WHERE id = ${srfId}` },
          { headers: { requestType: "fetchData" } },
        );

        const serviceReqNumber = serviceReqData?.[0]?.serviceReqNumber;

        // 3️⃣ Upload Instruments
        const ducInwardData = srfInstrumentsData.map((item) => ({
          ...item,
          srfId,
          serviceReqNumber,
        }));

        const { data: ducRes } = await axiosWithToken.post(
          `${BASE_URL}srfs/ducInward`,
          { srfId, ducInwardData, schema: createSchema },
        );

        const srfInstrumentIdsList = ducRes?.srfInstrumentsInsertIds;

        await createSRFAnalytics(srfId, srfInstrumentIdsList, 25);

        // 4️⃣ Patch datasheets, certificates, readings
        for (let i = 0; i < srfInstrumentIdsList.length; i++) {
          const srfInstrumentId = srfInstrumentIdsList[i];

          // 📄 Update Datasheet
          const datasheet = { ...datasheetsData[i] };
          const suffix = datasheet.JobNumber?.split("/")?.[1] || "1";

          delete datasheet.JobNumber;
          delete datasheet.serviceReqNumber;
          delete datasheet.id;

          datasheet.serviceReqNumber = serviceReqNumber;
          datasheet.JobNumber = `${serviceReqNumber
            .trim()
            .split("/")
            .pop()}/${suffix}`;

          await axiosWithToken.patch(
            `${BASE_URL}datasheets/${srfInstrumentId}`,
            datasheet,
          );

          // 📊 Static Readings
          const readings = datasheetStaticReadingsData?.[i]?.map(
            ({ id, ...rest }) => ({
              ...rest,
              datasheetId: srfInstrumentId,
            }),
          );

          if (readings?.length) {
            await axiosWithToken.post(
              `${BASE_URL}datasheetStaticReadings/bulk`,
              readings,
            );
          }

          // 📜 Update Certificate
          const certificate = { ...certificatesData[i] };

          delete certificate.certificateNumber;
          delete certificate.serviceReqNumber;
          delete certificate.ULRNo;
          delete certificate.id;

          certificate.serviceReqNumber = serviceReqNumber;
          certificate.certificateNumber = `${serviceReqNumber}/${suffix}`;

          await axiosWithToken.patch(
            `${BASE_URL}certificates/${srfInstrumentId}`,
            certificate,
          );
        }
      }
    }

    // ✅ Step 3: Patch datasheetIds related tables
    if (datasheetIds.length) {
      for (const row of datasheetsRows) {
        const { id, ...payload } = row;
        await axiosWithToken.patch(`${BASE_URL}datasheets/${id}`, payload);
      }

      for (const row of certificatesRows) {
        const { id, ...payload } = row;
        await axiosWithToken.patch(`${BASE_URL}certificates/${id}`, payload);
      }

      if (readingsRows.length) {
        await axiosWithToken.post(
          `${BASE_URL}datasheetStaticReadings/bulk`,
          readingsRows,
        );
      }
    }
  } catch (error) {
    console.error("Error submitting offline data to server:", error);
  } finally {
    await clearTablesInSqlJs();
    setLoader(false);
  }
};

export const clearTablesInSqlJs = async () => {
  const tablesToClear = [
    "srfs",
    "srfInstruments",
    "datasheets",
    "certificates",
    "datasheetStaticReadings",
  ];

  for (const table of tablesToClear) {
    const query = `DELETE FROM ${table}`;
    const { data } = await executeSqlInSqlJs(query);

    if (data.affectedRows > 0) {
      console.log(`Cleared ${data.affectedRows} rows from "${table}"`);
    } else {
      console.log(`No rows found in "${table}" or already empty.`);
    }
  }

  console.log("All specified tables have been cleared.");
};
